import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { crewService } from '@services/crew';
import { CREW_QUERY_KEY } from './useCrew';
import { CREWS_BY_USER_QUERY_KEY } from './useCrews';
import { ASSIGNABLE_TASKS_QUERY_KEY } from './useAssignableTasks';
import { useTranslation } from '@hooks/useTranslation';

export type AssignTasksArgs = {
  crewId: string;
  /** The crew's desired task set. An empty array clears every assignment. */
  taskIds: string[];
};

export const useAssignTasksToCrew = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['crew', 'assignTasks'],
    mutationFn: async ({ crewId, taskIds }: AssignTasksArgs) => {
      if (!crewId) throw new Error('crewId is required');
      return await crewService.assignTasksToCrew(crewId, taskIds);
    },
    onSuccess: (crew) => {
      toast.success(tActionMessages.crewTasksUpdatedSuccessfully);
      queryClient.invalidateQueries({ queryKey: CREW_QUERY_KEY(crew.crewId) });
      // A task can only have one crew, so moving one changes what *every* crew
      // holds — not just this one.
      queryClient.invalidateQueries({ queryKey: CREWS_BY_USER_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ASSIGNABLE_TASKS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.errors?.[0]?.message ||
          error?.response?.data?.message ||
          error?.message ||
          tActionMessages.requestError,
      );
    },
  });
};

export default useAssignTasksToCrew;
